/* sc-pz-magsafe-cart-guard.js — cart-page guard #2 against a double MagSafe charge.
 *
 * The PZ MagSafe checkbox is charged as a companion line (product handle
 * `make-it-magsafe`, +$4.99) added atomically with the design line and bound to it by the
 * runtime via `_pz_parent_operation_id` (the parent PZ line carries the matching
 * `_pz_operation_id`). The runtime is the PRIMARY guard: it hides the checkbox on the
 * MagSafe-bundling variant ("MagSafe Tough Case"), so a fresh add-to-cart can never carry both
 * the variant premium AND the companion.
 *
 * This file is the SECOND guard, for the one path the runtime cannot reach: an Edit-Design
 * variant change (merchant-flagged) that moves an EXISTING design line onto the MagSafe Tough
 * Case variant while its companion line stays in the cart. That line would then be charged twice
 * — once inside the variant, once by the stale companion. On the cart/drawer this guard finds any
 * `make-it-magsafe` line whose bound parent design line is on a MagSafe variant and removes it.
 *
 * Safety: it acts only on the exact stale companion (matched by the durable design-group stamp
 * or the parent-operation stamp — the group leg matters because an edit rotates the parent's
 * operation id while the companion's stamps stay frozen) and mutates
 * by line KEY, never by index, so a concurrent theme mutation cannot shift it onto the wrong line.
 * It waits for `window.__scCartMutationPending` to clear first. On any product without MagSafe it
 * self-bails. Removing a companion is the same net effect as never having ticked it.
 */
(function () {
  'use strict';

  var COMPANION_HANDLE = 'make-it-magsafe';
  var MAGSAFE_VARIANT_RE = /magsafe|magnetic/i; // owner rule: parent variant = "MagSafe Tough Case" (iPhone) or "Tough Magnetic Case" (Samsung, Albert 13:08 04/11)
  var IDLE_WAIT_MS = 5000;
  var REQUEST_DEADLINE_MS = 4000;
  var FAILURE_RETRY_MS = 250;

  var arrayIsArray = Array.isArray;
  var hasOwnCall = Function.prototype.call.bind(Object.prototype.hasOwnProperty);
  var arrayPushCall = Function.prototype.call.bind(Array.prototype.push);
  var regexpTestCall = Function.prototype.call.bind(RegExp.prototype.test);
  var stringSplitCall = Function.prototype.call.bind(String.prototype.split);
  var stringValue = String;
  function ownValue(object, key) {
    return object && hasOwnCall(object, key) ? object[key] : undefined;
  }

  // ---- pure detection (unit-tested offline) ------------------------------------------------
  function itemProps(item) {
    var value = ownValue(item, 'properties');
    return value && typeof value === 'object' && !arrayIsArray(value) ? value : {};
  }

  // A design line advertises its bindings; the variant it is charged on carries the MagSafe
  // premium when its variant title says so. Only lines that both bundle MagSafe AND own a bound
  // companion are a double charge. BOTH bindings of such a parent are collected: the current
  // operation id AND the durable design group (birth operation, carried through every edit) —
  // an edit rotates the operation id without touching the companion's stamps, so a companion
  // stamped before the edit only matches through the group.
  function parentOperationIdsByBundling(items, bundled) {
    var ids = Object.create(null);
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var props = itemProps(it);
      var opId = ownValue(props, '_pz_operation_id');
      if (!opId) continue;
      var variantTitle = ownValue(it, 'variant_title');
      var title = stringValue(variantTitle == null ? '' : variantTitle);
      if (regexpTestCall(MAGSAFE_VARIANT_RE, title) !== bundled) continue;
      ids[stringValue(opId)] = true;
      ids[stringValue(ownValue(props, '_pz_design_group_id') || opId)] = true;
    }
    return ids;
  }

  function magsafeParentOperationIds(items) {
    return parentOperationIdsByBundling(items, true);
  }

  function regularParentOperationIds(items) {
    return parentOperationIdsByBundling(items, false);
  }

  // Returns the line KEYS of every `make-it-magsafe` companion bound to a design line that is
  // itself on a MagSafe variant — i.e. the redundant, double-charging lines. A companion proves
  // the binding through its durable group stamp or its parent-operation stamp (which may be a
  // stale birth operation the parent's group still names).
  function findStaleMagsafeKeys(cart) {
    var cartItems = ownValue(cart, 'items');
    var items = arrayIsArray(cartItems) ? cartItems : [];
    var magsafeParents = magsafeParentOperationIds(items);
    var regularParents = regularParentOperationIds(items);
    var stale = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var product = ownValue(it, 'product');
      var handle = ownValue(product, 'handle') || ownValue(it, 'handle') || '';
      if (stringValue(handle) !== COMPANION_HANDLE) continue;
      var props = itemProps(it);
      var group = ownValue(props, '_pz_design_group_id');
      var parentOp = ownValue(props, '_pz_parent_operation_id');
      var bound = (group && magsafeParents[stringValue(group)])
        || (parentOp && magsafeParents[stringValue(parentOp)]);
      if (!bound) continue;
      // Q7 is deliberately ADD-FIRST: for a short, authoritative-cart interval both the old
      // and new parent carry the same durable design group. When the transition is bundled ->
      // regular, the newly-added paid companion belongs to the regular parent even though its
      // group also matches the old bundled parent. Removing it here races the transport and
      // silently drops $4.99. Defer whenever a regular parent owns either binding; after the old
      // parent retires the companion remains correctly paid, while a truly stale companion on a
      // lone bundled parent has no regular veto and is still removed on the next cart render.
      var boundToRegular = (group && regularParents[stringValue(group)])
        || (parentOp && regularParents[stringValue(parentOp)]);
      if (boundToRegular) continue;
      var key = ownValue(it, 'key');
      if (key) arrayPushCall(stale, stringValue(key));
    }
    return stale;
  }

  // The cart contains reservation timers, loading labels and third-party widgets that mutate
  // continuously. Observing every child-list edit turned this safety guard into an unbounded
  // /cart.js poller. Only a mutation that adds/removes an actual cart row can change the stale
  // companion verdict; text/clock/widget churn is ignored.
  function nodeContainsCartRow(node) {
    if (!node || (node.nodeType !== 1 && node.nodeType !== 11)) return false;
    if (node.nodeType === 1 && node.matches
      && node.matches('.cart-item, [data-cart-item-key], cart-items, cart-drawer-items')) return true;
    return !!(node.querySelector
      && node.querySelector('.cart-item, [data-cart-item-key], cart-items, cart-drawer-items'));
  }

  function mutationTouchesCartRows(records) {
    if (!records || typeof records.length !== 'number') return false;
    for (var i = 0; i < records.length; i++) {
      var record = records[i] || {};
      var added = record.addedNodes || [];
      var removed = record.removedNodes || [];
      for (var a = 0; a < added.length; a++) if (nodeContainsCartRow(added[a])) return true;
      for (var r = 0; r < removed.length; r++) if (nodeContainsCartRow(removed[r])) return true;
    }
    return false;
  }

  // Expose the pure core for the offline contract test.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      findStaleMagsafeKeys: findStaleMagsafeKeys,
      magsafeParentOperationIds: magsafeParentOperationIds,
      mutationTouchesCartRows: mutationTouchesCartRows
    };
    return;
  }

  var fetchFrame = null;
  var fetchRealm = null;
  var nativeFetch = null;
  var responseOk = null;
  var responseText = null;
  var jsonParse = null;
  var jsonStringify = null;
  var cleanAssign = null;
  var encodeValue = encodeURIComponent;
  var AbortCtor = null;
  try {
    fetchFrame = document.createElement('iframe');
    fetchFrame.hidden = true;
    fetchFrame.tabIndex = -1;
    fetchFrame.setAttribute('aria-hidden', 'true');
    if (fetchFrame.style) fetchFrame.style.display = 'none';
    document.documentElement.appendChild(fetchFrame);
    fetchRealm = fetchFrame.contentWindow;
    var responseProto = fetchRealm && fetchRealm.Response && fetchRealm.Response.prototype;
    var functionProto = fetchRealm && fetchRealm.Function && fetchRealm.Function.prototype;
    var CleanObject = fetchRealm && fetchRealm.Object;
    var CleanArray = fetchRealm && fetchRealm.Array;
    if (!fetchRealm || typeof fetchRealm.fetch !== 'function' || !fetchRealm.JSON
      || !responseProto || !functionProto || !CleanObject || !CleanArray
      || typeof responseProto.text !== 'function') throw new Error('clean-cart-fetch-unavailable');
    function bindCall(fn) { return functionProto.call.bind(fn); }
    function getter(proto, name) {
      var cursor = proto;
      while (cursor) {
        var descriptor = CleanObject.getOwnPropertyDescriptor(cursor, name);
        if (descriptor && typeof descriptor.get === 'function') return bindCall(descriptor.get);
        cursor = CleanObject.getPrototypeOf(cursor);
      }
      return null;
    }
    var callFetch = bindCall(fetchRealm.fetch);
    nativeFetch = function (url, options) { return callFetch(fetchRealm, url, options); };
    responseOk = getter(responseProto, 'ok');
    responseText = bindCall(responseProto.text);
    if (!responseOk || !responseText) throw new Error('clean-cart-response-unavailable');
    var callJsonParse = bindCall(fetchRealm.JSON.parse);
    var callJsonStringify = bindCall(fetchRealm.JSON.stringify);
    var cleanKeys = CleanObject.keys;
    var cleanDefine = CleanObject.defineProperty;
    function localJson(value, depth, count) {
      if (++count.n > 50000 || depth > 64) throw new Error('cart-json-limit');
      var type = typeof value;
      if (value === null || type === 'string' || type === 'boolean' || type === 'number') return value;
      if (type !== 'object') throw new Error('cart-json-type');
      var array = CleanArray.isArray(value); var output = array ? [] : {}; var keys = cleanKeys(value);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i]; var child = localJson(value[key], depth + 1, count);
        if (array) output[key] = child;
        else cleanDefine(output, key, { value: child, enumerable: true, writable: true, configurable: true });
      }
      return output;
    }
    jsonParse = function (text) { return localJson(callJsonParse(fetchRealm.JSON, text), 0, { n: 0 }); };
    jsonStringify = function (value) { return callJsonStringify(fetchRealm.JSON, value); };
    cleanAssign = CleanObject.assign;
    arrayIsArray = CleanArray.isArray;
    hasOwnCall = bindCall(CleanObject.prototype.hasOwnProperty);
    arrayPushCall = bindCall(CleanArray.prototype.push);
    regexpTestCall = bindCall(fetchRealm.RegExp.prototype.test);
    stringSplitCall = bindCall(fetchRealm.String.prototype.split);
    stringValue = fetchRealm.String;
    if (typeof fetchRealm.encodeURIComponent === 'function') encodeValue = fetchRealm.encodeURIComponent;
    AbortCtor = fetchRealm.AbortController || null;
  } catch (transportError) {
    try { if (fetchFrame && fetchFrame.parentNode) fetchFrame.parentNode.removeChild(fetchFrame); } catch (_) {}
    fetchFrame = null;
  }

  // ---- cart surfaces + swap (mirrors sc-cart-protection-toggle) -----------------------------
  function pageItemsSectionId() {
    var el = document.getElementById('main-cart-items');
    return el ? el.getAttribute('data-id') : null;
  }
  function pageFooterSectionId() {
    var el = document.getElementById('main-cart-footer');
    return el ? el.getAttribute('data-id') : null;
  }
  function sectionsToRender() {
    var ids = ['cart-drawer', 'cart-icon-bubble'];
    var pageId = pageItemsSectionId();
    if (pageId) arrayPushCall(ids, pageId);
    var footerId = pageFooterSectionId();
    if (footerId) arrayPushCall(ids, footerId);
    return ids.join(',');
  }
  function swapSections(sections, itemCount) {
    if (!sections) return;
    var drawerHtml = ownValue(sections, 'cart-drawer');
    if (typeof drawerHtml === 'string') {
      var holder = document.createElement('div');
      holder.innerHTML = drawerHtml;
      var freshInner = holder.querySelector('.drawer__inner');
      var currentInner = document.querySelector('cart-drawer .drawer__inner');
      if (freshInner && currentInner) currentInner.innerHTML = freshInner.innerHTML;
      if (window.scCartRowOrder) window.scCartRowOrder.apply();
    }
    var pageId = pageItemsSectionId();
    var pageHtml = pageId && ownValue(sections, pageId);
    if (pageId && typeof pageHtml === 'string') {
      var pageHolder = document.createElement('div');
      pageHolder.innerHTML = pageHtml;
      var freshContents = pageHolder.querySelector('#main-cart-items .js-contents');
      var currentContents = document.querySelector('#main-cart-items .js-contents');
      if (freshContents && currentContents) currentContents.innerHTML = freshContents.innerHTML;
    }
    var footerId = pageFooterSectionId();
    var footerHtml = footerId && ownValue(sections, footerId);
    if (footerId && typeof footerHtml === 'string') {
      var footerHolder = document.createElement('div');
      footerHolder.innerHTML = footerHtml;
      var freshFooter = footerHolder.querySelector('.cart__blocks .js-contents');
      var currentFooter = document.querySelector('#main-cart-footer .cart__blocks .js-contents');
      if (freshFooter && currentFooter) currentFooter.innerHTML = freshFooter.innerHTML;
    }
    var bubbleHtml = ownValue(sections, 'cart-icon-bubble');
    if (typeof bubbleHtml === 'string') {
      var bubbleHolder = document.createElement('div');
      bubbleHolder.innerHTML = bubbleHtml;
      var bubble = document.getElementById('cart-icon-bubble');
      var freshBubble = bubbleHolder.querySelector('#shopify-section-cart-icon-bubble') || bubbleHolder;
      if (bubble) bubble.innerHTML = freshBubble.innerHTML;
    }
    if (typeof itemCount === 'number') {
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', itemCount === 0);
    }
  }

  function whenCartIdle(maxWaitMs) {
    return new Promise(function (resolve) {
      var waited = 0;
      (function check() {
        if (!window.__scCartMutationPending) { resolve(true); return; }
        if (waited >= maxWaitMs) { resolve(false); return; }
        waited += 120;
        setTimeout(check, 120);
      })();
    });
  }

  // Bound both response headers and JSON decoding. AbortController only saves resources; the
  // Promise deadline is the authority, so an old browser or a body that never settles cannot
  // leave `running` stuck forever and swallow every later cart-row signal.
  function fetchWithDeadline(url, options, deadlineMs, parseJson) {
    var ac = typeof AbortCtor === 'function' ? new AbortCtor() : null;
    var requestOptions = cleanAssign({}, options || {});
    requestOptions.credentials = 'same-origin';
    requestOptions.redirect = 'error';
    requestOptions.cache = 'no-store';
    if (ac) requestOptions.signal = ac.signal;
    var timer = null;
    var timeoutFlight = new Promise(function (resolve, reject) {
      timer = window.setTimeout(function () {
        if (ac) { try { ac.abort(); } catch (e) { /* deadline rejects below */ } }
        reject(new Error('cart request deadline'));
      }, deadlineMs);
    });
    var requestFlight;
    try {
      if (!nativeFetch || !responseOk || !responseText || !jsonParse) throw new Error('cart transport unavailable');
      requestFlight = Promise.resolve(nativeFetch(url, requestOptions));
    } catch (e) {
      requestFlight = Promise.reject(e);
    }
    requestFlight = requestFlight.then(function (res) {
      if (!res || responseOk(res) !== true) throw new Error('cart request failed');
      if (parseJson !== true) return null;
      return Promise.resolve(responseText(res)).then(function (text) { return jsonParse(text); });
    });
    return Promise.race([requestFlight, timeoutFlight]).then(function (json) {
      window.clearTimeout(timer);
      return json;
    }, function (error) {
      window.clearTimeout(timer);
      throw error;
    });
  }

  function routeRoot() {
    var shopify = ownValue(window, 'Shopify');
    var routes = ownValue(shopify, 'routes');
    var raw = stringValue(ownValue(routes, 'root') || '/');
    return (raw === '/' || /^\/[A-Za-z0-9._~\/-]*\/$/.test(raw)) && raw.indexOf('//') === -1
      && raw.indexOf('..') === -1 ? raw : '/';
  }
  function cartHasKey(cart, key) {
    var items = ownValue(cart, 'items');
    if (!arrayIsArray(items)) throw new Error('cart items invalid');
    for (var i = 0; i < items.length; i++) {
      if (stringValue(ownValue(items[i], 'key') || '') === key) return true;
    }
    return false;
  }
  function removeStale(cart, remaining) {
    var stale = findStaleMagsafeKeys(cart);
    if (!stale.length) return Promise.resolve(cart);
    if (remaining <= 0) return Promise.reject(new Error('stale companion removal bound exceeded'));
    var key = stale[0];
    return fetchWithDeadline(routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: jsonStringify({ id: key, quantity: 0 })
    }, REQUEST_DEADLINE_MS, false).then(function () {
      // Mutation response HTML/cart is never authority. Re-prove the exact key disappeared from
      // a fresh no-store cart before considering another removal or presenting any section.
      return fetchWithDeadline(routeRoot() + 'cart.js', {
        headers: { Accept: 'application/json' }
      }, REQUEST_DEADLINE_MS, true);
    }).then(function (freshCart) {
      if (cartHasKey(freshCart, key)) throw new Error('stale companion removal not proved');
      return removeStale(freshCart, remaining - 1);
    });
  }
  function refreshCurrentSections(cart) {
    var ids = sectionsToRender();
    return fetchWithDeadline(routeRoot() + '?sections=' + encodeValue(ids), {
      headers: { Accept: 'application/json' }
    }, REQUEST_DEADLINE_MS, true).then(function (sections) {
      var required = stringSplitCall(ids, ',');
      for (var i = 0; i < required.length; i++) {
        if (typeof ownValue(sections, required[i]) !== 'string') throw new Error('current cart section missing');
      }
      var itemCount = ownValue(cart, 'item_count');
      if (!Number.isSafeInteger(itemCount) || itemCount < 0) throw new Error('cart item count invalid');
      swapSections(sections, itemCount);
      return cart;
    });
  }

  // ---- reconcile ---------------------------------------------------------------------------
  var running = false;
  var rerunRequested = false;
  function reconcile(attempt) {
    attempt = attempt || 0;
    // A cart-row replacement can land while the authoritative read is in flight. Dropping that
    // signal is unsafe: the first read may see the deliberate two-parent overlap and defer the
    // removal, while the replacement is the only signal that the old parent has now retired.
    // Latch one rerun instead of relying on unrelated timer/widget mutations to wake us later.
    if (running) {
      rerunRequested = true;
      return;
    }
    running = true;
    var failed = false;
    whenCartIdle(IDLE_WAIT_MS).then(function () {
      return fetchWithDeadline(routeRoot() + 'cart.js', {
        headers: { Accept: 'application/json' }
      }, REQUEST_DEADLINE_MS, true);
    }).then(function (cart) {
      var stale = findStaleMagsafeKeys(cart);
      if (!stale.length) return null;
      var bound = stale.length + 4;
      return removeStale(cart, bound < 20 ? bound : 20).then(refreshCurrentSections);
    }).catch(function () {
      // A guard failure must never block the cart. Retry exactly once after a short delay: after
      // mutation filtering there may be no incidental DOM churn to trigger another read, but an
      // unbounded poller would be just as unsafe. A later real row replacement starts a fresh pair.
      failed = true;
    }).then(function () {
      running = false;
      if (rerunRequested) {
        rerunRequested = false;
        schedule();
      } else if (failed && attempt === 0) {
        window.setTimeout(function () { reconcile(1); }, FAILURE_RETRY_MS);
      }
    });
  }

  // Run on load and whenever the drawer/cart markup re-renders (Edit Design replaces the line).
  //
  // Coalescing (owner performance lane 2026-08-20, dedupe-only): reservation-timer widgets
  // re-render cart rows every second, which slipped through the row filter and turned this
  // guard into a steady ~2 reads/s of /cart.js on every PDP (measured 20 GETs in one ATC
  // window). The FIRST trigger still reconciles immediately (leading edge — a real Edit
  // row-replacement keeps today's latency); every further trigger inside the window folds
  // into ONE trailing reconcile. Same verdict, same removal semantics, strictly fewer reads.
  var COALESCE_MS = 4000;
  var scheduled = false;
  var lastReconcileAt = 0;
  var trailingTimer = null;
  function runNow() {
    lastReconcileAt = Date.now();
    reconcile();
  }
  var lastMutationSeenAt = 0;
  function schedule() {
    if (scheduled) return;
    // A REAL cart mutation (Edit exact-line change, add, remove) sets the existing
    // __scCartMutationPending flag; while it is (or was just) set, the guard keeps its
    // original immediate response — only flag-free churn (reservation timers, widgets)
    // is coalesced. Reading an existing signal, no new machinery (PI gate 20/08).
    if (window.__scCartMutationPending) lastMutationSeenAt = Date.now();
    var since = Date.now() - lastReconcileAt;
    if (Date.now() - lastMutationSeenAt < COALESCE_MS) since = COALESCE_MS;
    if (since >= COALESCE_MS) {
      scheduled = true;
      (window.requestAnimationFrame || window.setTimeout)(function () {
        scheduled = false;
        runNow();
      }, 0);
      return;
    }
    if (trailingTimer) return; // một trailing run là đủ cho mọi churn trong cửa sổ
    trailingTimer = window.setTimeout(function () {
      trailingTimer = null;
      runNow();
    }, COALESCE_MS - since);
  }

  function watch() {
    var root = document.querySelector('cart-drawer') || document.querySelector('cart-items') || document.body;
    if (!root) return;
    var observer = new MutationObserver(function (records) {
      if (mutationTouchesCartRows(records)) schedule();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    schedule();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
